import { useCallback, useEffect, useState } from "react";
import { getHomework } from "../../../../services/StudentPortal/StudentHomeworkService";
import { unwrap } from "../../utils/studentPortalUtils";
import { handleApiError } from "../../../../utils/errorHandler";

// Confirmed against the real backend (2026-10-22): the response is NOT a
// flat list (unwrapList would always return [] for it, since it's an
// object, not an array) — it's
// { organization, session, program, daily: [...], weekly: { "<start> to
// <end>": [...] } }. "daily" holds single-day homework items; "weekly"
// groups multi-day homework by its date-range key. Both are flattened into
// one list here, each item tagged with its own date range, so the page can
// render them uniformly regardless of which bucket they came from.
const flattenHomeworkPayload = (payload) => {
  if (!payload || typeof payload !== "object") return [];

  const daily = Array.isArray(payload.daily) ? payload.daily : [];
  const dailyItems = daily.map((item) => ({ ...item, dateRangeKey: null }));

  const weekly = payload.weekly && typeof payload.weekly === "object" ? payload.weekly : {};
  const weeklyItems = Object.entries(weekly).flatMap(([rangeKey, items]) =>
    (Array.isArray(items) ? items : []).map((item) => ({ ...item, dateRangeKey: rangeKey }))
  );

  return [...dailyItems, ...weeklyItems];
};

// Confirmed against the real backend (2026-10-22): GET student/homework 422s
// without BOTH `program_id` and `homework_date` — it isn't scoped by the
// authenticated student_id alone like most other endpoints, so both are
// required inputs here rather than optional params.
export const useStudentHomework = (programId, homeworkDate) => {
  const [homeworkItems, setHomeworkItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchHomework = useCallback(async () => {
    if (!programId || !homeworkDate) {
      setHomeworkItems([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await getHomework({ program_id: programId, homework_date: homeworkDate });
      setHomeworkItems(flattenHomeworkPayload(unwrap(response, null)));
    } catch (error) {
      handleApiError(error);
      setHomeworkItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [programId, homeworkDate]);

  useEffect(() => {
    fetchHomework();
  }, [fetchHomework]);

  return { homeworkItems, isLoading };
};

export default useStudentHomework;
